<template>
    <div class="overflow-hidden lg:overflow-visible">
        <div
            class="type-11-block container swiper-container grid grid-cols-12 base-responsive-gap relative pt-20 xl:pt-32"
        >
            <slot name="index"></slot>
            <div
                ref="swiper"
                class="swiper swiper--block-11 mx-[-10px] px-[10px] order-4 lg:order-2 col-span-12 lg:col-span-4 lg:col-start-2 lg:py-24 relative z-3"
            >
                <div ref="panels" class="swiper-wrapper lg:border-t-[1px] lg:border-gray-600 pt-2 lg:pt-5">
                    <div
                        v-for="(slide, i) in slides"
                        :id="`m${i + 1}`"
                        :key="`type11-slide-${i}`"
                        class="content-panel swiper-slide"
                    >
                        <div class="content-panel__control-guide-line"></div>
                        <span class="content-panel__control" :class="{ active: activePanelID === i + 1 }"></span>
                        <h2 class="mb-2" :class="i === 0 ? 'header-1 lg:mb-8' : 'header-2'">
                            <T :t="slide.titleKey" />
                        </h2>
                        <p class="whitespace-pre-line">
                            <T :t="slide.descriptionKey" />
                        </p>
                    </div>
                </div>
            </div>
            <div class="order-3 col-span-12 carousel-pagination-block container relative lg:hidden z-2">
                <div class="flex items-center justify-center">
                    <button ref="prevBtn" class="swiper-button-prev"></button>
                    <span class="gallery-pagination-counter">01</span>
                    <div ref="pagination" class="swiper-pagination"></div>
                    <span class="gallery-pagination-counter">05</span>
                    <button ref="nextBtn" class="swiper-button-next"></button>
                </div>
            </div>
            <div
                class="order-2 lg:order-3 col-span-12 sm:col-span-10 sm:col-start-2 md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-7 mb-4 lg:mb-0 relative z-1"
            >
                <div class="aspect-w-1 aspect-h-1 rounded-full overflow-hidden sticky top-[20vh]">
                    <div
                        v-for="(slide, i) in slides"
                        :key="`type11-image-${i}`"
                        class="content-panel__image"
                        :class="{
                            'translate-y-0': activePanelID === i + 1,
                            'translate-y-full': activePanelID < i + 1,
                            '-translate-y-12': activePanelID > i + 1,
                        }"
                    >
                        <StaticImage :img-key="slide.imageKey" wrapper-class="h-full" class="h-full" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { throttle } from 'throttle-debounce';

export default {
    name: 'Type11Block',
    props: {
        slides: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            swiper: null,
            activePanelID: 1,
            scrollEventHandler: throttle(100, false, this.setActivePanelID),
        };
    },
    watch: {
        '$store.state.windowWidth'(value) {
            if (value < 1024 && !this.swiper) {
                this.createSwiper();
                this.activePanelID = 1;
            } else if (value >= 1024 && this.swiper) {
                this.swiper.destroy();
                this.swiper = null;
            }
        },
    },
    mounted() {
        document.addEventListener('scroll', this.setActivePanelID);

        if (window.innerWidth < 1024) {
            this.createSwiper();
        }
    },
    beforeDestroy() {
        document.removeEventListener('scroll', this.setActivePanelID);
        if (this.swiper) {
            this.swiper.destroy();
        }
    },
    methods: {
        createSwiper() {
            this.swiper = new this.$swiper(this.$refs.swiper, {
                spaceBetween: 16,
                slidesPerView: 1,
                centeredSlides: false,
                loop: false,
                navigation: {
                    prevEl: this.$refs.prevBtn,
                    nextEl: this.$refs.nextBtn,
                },
                pagination: {
                    el: this.$refs.pagination,
                    clickable: true,
                    bulletElement: 'button',
                },
                a11y: {
                    containerMessage: this.$t('carousel.a11y.container_message_gallery'),
                    paginationBulletMessage: this.$t('carousel.a11y.pagination_bullet_message'),
                    firstSlideMessage: this.$t('carousel.a11y.first_slide_message'),
                    lastSlideMessage: this.$t('carousel.a11y.last_slide_message'),
                    nextSlideMessage: this.$t('carousel.a11y.next_slide_message'),
                    prevSlideMessage: this.$t('carousel.a11y.prev_slide_message'),
                },
                // breakpoints: {
                //     640: {
                //         spaceBetween: 30,
                //         slidesPerView: 3,
                //     },
                // },
            });

            this.swiper.on('slideChange', () => {
                this.activePanelID = this.swiper.realIndex + 1;
            });
        },
        setActivePanelID() {
            if (window.innerWidth < 1024) {
                return;
            }

            let _tempActivePanelID;

            Array.from(this.$refs.panels.children).forEach((panel, i) => {
                if (_tempActivePanelID) {
                    return;
                }

                if (panel.getBoundingClientRect().bottom > window.innerHeight / 2) {
                    _tempActivePanelID = i + 1;
                }
            });

            if (_tempActivePanelID) {
                this.activePanelID = _tempActivePanelID;
            }
        },
    },
};
</script>

<style>
.swiper--block-11 {
    @media (min-width: 1024px) {
        overflow: visible;
        .swiper {
            overflow: visible;
        }
        .swiper-wrapper {
            flex-direction: column;
        }
        .swiper-slide {
            height: unset;
        }
    }
}

.type-11-block .swiper {
    overflow: visible;
}

.type-11-block .swiper-slide::before {
    content: '';
    @apply block absolute w-full h-[calc(100vw)] -top-20 left-0 -translate-y-full lg:hidden;
}

.content-panel {
    @apply relative flex flex-col;
    h2,
    p {
        @apply text-center lg:text-left;
    }
    &:not(:first-child) {
        @apply lg:py-12;
    }

    .content-panel__control-guide-line {
        @apply hidden lg:block absolute w-[1px] bg-black left-[-53px] h-full top-0;
    }
    &:first-child,
    &:last-child {
        .content-panel__control-guide-line {
            @apply h-1/2;
        }
    }
    &:first-child {
        @apply lg:pb-12;
        .content-panel__control-guide-line {
            top: unset;
            bottom: 0;
        }
    }
}

.content-panel__control {
    @apply hidden lg:block absolute top-1/2 -translate-y-1/2 left-[-60px] w-4 h-4 rounded-full bg-gray-100 border border-black transition-colors;
    &.active {
        @apply bg-black;
    }
}

.content-panel__image {
    @apply absolute top-0 left-0 w-full h-full rounded-full overflow-hidden transition-transform duration-[500ms];
    img {
        @apply w-full h-full object-cover object-center;
    }
}
</style>
